import type { RowAction } from '@/components/ui/data-table/types';
import type { PanelAction } from '@/components/Layout/Panel/Panel';

/**
 * Convert row actions to panel actions for a specific entity
 *
 * This is a generic utility that adapts table row actions (which take an entity parameter)
 * to panel actions (which are already bound to a specific entity instance).
 *
 * @param rowActions - Array of row actions from the entity's action definitions
 * @param entity - The specific entity instance to bind the actions to
 * @returns Array of panel actions ready to be used in a Panel component
 */
export function adaptActionsForPanel<T>(
  rowActions: RowAction<T>[],
  entity: T,
): PanelAction[] {
  return rowActions.flatMap((action) => {
    const sublabel = action.sublabel;
    const disabled = action.disabled;
    const isDisabled =
      typeof disabled === 'function' ? disabled(entity) : disabled;
    if (action.hideWhenDisabled && isDisabled) return [];
    return [
      {
        icon: action.icon,
        label:
          typeof action.label === 'function'
            ? action.label(entity)
            : action.label,
        sublabel: typeof sublabel === 'function' ? sublabel(entity) : sublabel,
        variant: action.variant,
        disabled: isDisabled,
        onCalled: () => action.onCalled(entity),
      },
    ];
  });
}
